<template>
    <div class="ProcessConformanceDrawerPanel">
        <div
            v-if="filteredTraces <= 0"
            class="text-caption text-grey-7"
        >
            {{ $t('visualization.filters.emptyModel') }}
        </div>

        <template v-else>
            <QBtn
                color="primary"
                noCaps
                outline
                icon="cloud_upload"
                :label="$t('projects.models.upload')"
                class="full-width q-mb-md"
                :disable="disableElements"
                @click="handleUploadItem"
            />

            <div v-if="isLoading" class="q-mb-sm">
                <q-linear-progress indeterminate color="primary" />
            </div>

            <div v-if="!isLoading && models.length === 0" class="q-mt-sm">
                <WText :size="15">
                    {{ $t('visualization.conformance.noModels') }}
                </WText>
                <WText :size="13" class="q-mt-xs">
                    {{ $t('visualization.conformance.noModelsUpload') }}
                </WText>
            </div>

            <div v-else class="q-mt-sm">
                <q-select
                    v-model="selectedModelIdProxy"
                    dense
                    outlined
                    emitValue
                    mapOptions
                    behavior="menu"
                    :disable="disableElements"
                    :options="models"
                    :label="$t('visualization.conformance.chooseBpmn')"
                    class="q-mb-sm"
                />

                <QBtn
                    color="primary"
                    noCaps
                    outline
                    icon="visibility"
                    :label="$t('projects.models.preview')"
                    class="full-width q-mb-md"
                    :disable="disableElements || !selectedModelIdProxy"
                    @click="handlePreview"
                />

                <q-expansion-item
                    v-if="selectedModelIdProxy"
                    icon="insights"
                    :label="$t('projects.models.drawer.summaryTitle')"
                    defaultOpened
                    class="ContextDrawerExpansion ContextDrawerExpansion--last"
                    headerClass="ContextDrawerExpansionHeader"
                >
                    <div class="ContextDrawerExpansionContent">
                        <div
                            v-if="summaryError"
                            class="text-negative text-caption q-mb-sm"
                        >
                            {{ summaryError }}
                        </div>

                        <div v-if="isLoadingSummary" class="q-mb-sm">
                            <q-linear-progress indeterminate color="primary" />
                        </div>

                        <div
                            v-if="processName"
                            class="InfoRow q-mb-sm"
                        >
                            <div class="InfoRowLabel text-caption">
                                {{ $t('general.name') }}
                            </div>
                            <div class="InfoRowValue text-body2 text-primary">
                                {{ processName }}
                            </div>
                        </div>

                        <div
                            v-for="row in summaryRows"
                            :key="row.key"
                            class="InfoRow q-mb-sm"
                        >
                            <div class="InfoRowLabel text-caption">
                                {{ row.label }}
                            </div>
                            <div class="InfoRowValue text-body2 text-primary">
                                {{ row.value }}
                            </div>
                        </div>
                    </div>
                </q-expansion-item>

                <UploadDialog
                    ref="uploadDialog"
                    @import="uploadModel"
                    @rejected-file="notifyRejected"
                />
            </div>
        </template>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError, notifySuccess, notify,
} from '@/api'
import { UploadDialog } from '@/screens/Project/Models/sections'

export default {
    name: 'ProcessConformanceDrawerPanel',
    components: { UploadDialog },
    props: {
        projectId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]).isRequired,
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]).isRequired,
        filteredTraces: VueTypes.number.def(0),
        disableElements: VueTypes.bool.def(false),
        modelId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
    },
    emits: ['on-model-selected', 'on-preview-model', 'on-models-loaded'],
    data() {
        return {
            isLoading: false,
            models: [],
            selectedModelId: this.modelId,
            selectedModelBpmn: '',
            processName: '',
            summary: null,
            summaryError: '',
            isLoadingSummary: false,
        }
    },
    computed: {
        storageKey() {
            return `process.conformance.selectedModel.${this.projectId}.${this.processId}`
        },
        selectedModelName() {
            return this.models.find((m) => `${m.value}` === `${this.selectedModelIdProxy}`)?.label
        },
        summaryRows() {
            const s = this.summary || {}
            return [
                { key: 'tasks', label: this.$t('projects.models.drawer.tasks'), value: s.tasks ?? '-' },
                { key: 'events', label: this.$t('projects.models.drawer.events'), value: s.events ?? '-' },
                { key: 'gateways', label: this.$t('projects.models.drawer.gateways'), value: s.gateways ?? '-' },
                { key: 'flows', label: this.$t('projects.models.drawer.flows'), value: s.flows ?? '-' },
                { key: 'pools', label: this.$t('projects.models.drawer.pools'), value: s.pools ?? '-' },
                { key: 'lanes', label: this.$t('projects.models.drawer.lanes'), value: s.lanes ?? '-' },
                { key: 'subprocesses', label: this.$t('projects.models.drawer.subprocesses'), value: s.subprocesses ?? '-' },
            ]
        },
        selectedModelIdProxy: {
            get() {
                return this.selectedModelId
            },
            set(value) {
                this.selectedModelId = value
                this.persistSelectedModelId(value)
                this.$emit('on-model-selected', {
                    modelId: value,
                    modelName: this.models.find((m) => `${m.value}` === `${value}`)?.label,
                })
            },
        },
    },
    watch: {
        modelId: {
            handler(value) {
                if (value === undefined || value === null || value === '') return
                if (`${value}` === `${this.selectedModelId}`) return
                // Only apply if exists in the current options.
                const exists = this.models.some((m) => `${m.value}` === `${value}`)
                if (exists) {
                    this.selectedModelId = value
                }
            },
            immediate: true,
        },
        filteredTraces() {
            // When filters change and we go from empty->non-empty, reload models.
            this.fetchModels()
        },
        selectedModelId: {
            handler() {
                this.fetchSelectedModelSummary()
            },
            immediate: true,
        },
    },
    mounted() {
        this.fetchModels()
    },
    methods: {
        safeLocalName(node) {
            return ((node && (node.localName || node.nodeName)) || '').toString()
        },
        countByPredicate(nodes, predicate) {
            let count = 0
            nodes.forEach((n) => {
                if (predicate(n)) count += 1
            })
            return count
        },
        resetSummary() {
            this.selectedModelBpmn = ''
            this.processName = ''
            this.summary = null
            this.summaryError = ''
        },
        extractSummaryFromXml(xml) {
            this.summary = null
            this.summaryError = ''
            this.processName = ''

            if (!xml) return

            try {
                const doc = new DOMParser().parseFromString(xml, 'text/xml')
                const parseError = doc.getElementsByTagName('parsererror')
                if (parseError && parseError.length) {
                    this.summaryError = this.$t('projects.models.drawer.parseError')
                    return
                }

                const all = Array.from(doc.getElementsByTagName('*'))

                const localLower = (n) => this.safeLocalName(n).toLowerCase()

                const processEl = all.find((n) => this.safeLocalName(n) === 'process')
                const processName = processEl?.getAttribute?.('name')
                if (processName) this.processName = processName

                const tasks = this.countByPredicate(all, (n) => {
                    const t = localLower(n)
                    return t === 'task' || t.endsWith('task')
                })
                const events = this.countByPredicate(all, (n) => localLower(n).endsWith('event'))
                const gateways = this.countByPredicate(all, (n) => localLower(n).endsWith('gateway'))
                const flows = this.countByPredicate(all, (n) => localLower(n) === 'sequenceflow')
                const pools = this.countByPredicate(all, (n) => localLower(n) === 'participant')
                const lanes = this.countByPredicate(all, (n) => localLower(n) === 'lane')
                const subprocesses = this.countByPredicate(all, (n) => localLower(n) === 'subprocess')

                this.summary = {
                    tasks,
                    events,
                    gateways,
                    flows,
                    pools,
                    lanes,
                    subprocesses,
                }
            } catch (e) {
                this.summaryError = this.$t('projects.models.drawer.parseError')
            }
        },
        async fetchSelectedModelSummary() {
            if (!this.selectedModelIdProxy) {
                this.resetSummary()
                return
            }

            this.isLoadingSummary = true
            this.summaryError = ''
            try {
                const { bpmn } = await apiRequest(Api().models.get({ modelId: this.selectedModelIdProxy }))
                this.selectedModelBpmn = bpmn || ''
                this.extractSummaryFromXml(this.selectedModelBpmn)
            } catch (e) {
                this.resetSummary()
                this.summaryError = this.$t('projects.models.drawer.parseError')
            } finally {
                this.isLoadingSummary = false
            }
        },
        persistSelectedModelId(value) {
            try {
                if (value === undefined || value === null || value === '') {
                    localStorage.removeItem(this.storageKey)
                    return
                }
                localStorage.setItem(this.storageKey, `${value}`)
            } catch (e) {
                // intentionally empty
            }
        },
        loadPersistedModelId() {
            try {
                return localStorage.getItem(this.storageKey)
            } catch (e) {
                return null
            }
        },
        async fetchModels() {
            if (this.filteredTraces <= 0) {
                this.models = []
                this.$emit('on-models-loaded', { count: 0, models: [] })
                return
            }

            this.isLoading = true
            const params = { projectId: this.projectId }
            apiRequest(Api().models.list(params))
                .then(({ data }) => {
                    this.models = (data || []).map((x) => ({ label: x.modelName, value: x.id }))
                    this.$emit('on-models-loaded', { count: this.models.length, models: this.models })

                    if (this.models.length === 0) {
                        this.selectedModelId = undefined
                        return
                    }

                    const persisted = this.loadPersistedModelId()
                    const preferred = this.modelId || persisted

                    const chosen = preferred && this.models.find((m) => `${m.value}` === `${preferred}`)
                        ? preferred
                        : this.models[0].value

                    // Use the setter to persist + emit.
                    this.selectedModelIdProxy = chosen
                })
                .catch((e) => {
                    notifyError(e)
                    this.$emit('on-models-loaded', { count: this.models.length, models: this.models })
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handlePreview() {
            if (!this.selectedModelIdProxy) return
            this.$emit('on-preview-model', {
                modelId: this.selectedModelIdProxy,
                modelName: this.selectedModelName,
            })
        },
        handleUploadItem() {
            this.$refs.uploadDialog.open()
        },
        async uploadModel({ modelName, file }) {
            this.isLoading = true
            const form = new FormData()
            form.append('file', file)
            form.append('name', modelName)
            form.append('projectUid', this.projectId)
            apiRequest(Api().models.create({ params: form }))
                .then(() => {
                    notifySuccess(this.$t('projects.models.notifications.created'))
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                    this.fetchModels()
                })
        },
        notifyRejected() {
            notify({
                message: this.$t('projects.models.modals.upload.rejected'),
            })
        },
    },
}
</script>

<style scoped lang="scss">
.ProcessConformanceDrawerPanel {
    width: 100%;
}

:deep(.ContextDrawerExpansion) {
    border-top: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansion.ContextDrawerExpansion--last) {
    border-bottom: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansionHeader .q-item__label) {
    font-weight: 700;
}

:deep(.ContextDrawerExpansionHeader .q-item__section--avatar .q-icon) {
    color: $primary;
}

.ContextDrawerExpansionContent {
    padding: 8px 16px 16px;
}

.InfoRow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
}

.InfoRowLabel {
    flex: 0 1 auto;
}

.InfoRowValue {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
    word-break: break-word;
}
</style>
